import React from "react";
import PageHeader from "../../../components/layout/PageHeader";
import BrandShowcase from "../components/BrandShowcase";

/**
 * My brand on a phone: what the screen sets up, and where to set it up.
 *
 * The tabs are gone from here — details, plans, services, the contact form,
 * the configuration and the policy documents. Every one of them is a long form
 * read against a document previewed beside it, and a phone can show one of
 * those two at a time; what it produced was six screens that could each be
 * half-used. So the phone is given the pitch instead, and told plainly where
 * the work happens. The desktop screen is untouched — see `MyBrandPage`, which
 * is what chooses between the two.
 *
 * Nothing here is a gate: this is the one screen in the app that a phone
 * genuinely cannot carry, which is why the note at the foot is a statement
 * rather than the "Switch to desktop" dialog the dashboard shows once a
 * sign-in. That one is a recommendation about where the work is easier and
 * dismisses; this is the answer to "where are the brand settings", and it
 * stays on screen.
 *
 * The account menu agrees with it: My brand is one row there now rather than a
 * row that opens six tabs — see `MobileAccountMenu`.
 */
const MyBrandMobilePage: React.FC = () => (
  // `min-h-full` rather than `h-full`: the band at the foot sits on the bottom
  // edge of the screen on an ordinary phone, and on a short one the page
  // scrolls to it rather than squeezing the artwork into what is left.
  <div className="flex min-h-full flex-col p-page pt-page-top">
    {/* "My brand" rather than the desktop's "My brand setup": there is no
        setting up on this screen, and the band at the foot is what says where
        it is done. The back arrow is the header's own — this screen is opened
        from the account menu, so it is never one a phone starts on. */}
    <PageHeader title="My brand" />

    {/* The showcase takes the screen between the title and the band, and the
        padding here is the least it may come to either of them. The artwork
        inside is what absorbs the difference between one phone and the next —
        see `BrandShowcase` — so this is a fit rather than a guess: the band
        below is on the bottom edge of the screen on every handset tall enough
        to hold the picture at all, and on one that is not, the page scrolls.
        A short phone keeps less of the padding, which is the first thing to
        give before the picture starts to. */}
    <div className="mt-6 flex min-h-0 flex-1 flex-col pb-8 short:mt-4 short:pb-5">
      <BrandShowcase />
    </div>

    {/* The honest answer, on the bottom edge of the screen.

        A band rather than a card: it is not a thing on the page, it is the
        foot of the page — it runs to both edges and sits under everything,
        which is what tells it apart from the three cards sliding above it.
        It keeps its own height and is measured first: the showcase above
        takes what is left, so the room a tall phone has to spare goes into
        the picture rather than into a gap under it.

        Lavender and purple: the one pair in the app that is neither the brand
        teal nor an alert, which is right for a note that is neither something
        to do nor something that has gone wrong. The same pair the account
        menu's avatar and the trial banner are drawn in.

        Trim rather than deep. It was drawn with the design's own air in it,
        which on a phone made a note read like an announcement: two large
        lines on a 136px band, and every pixel of it taken off the picture
        above — the one thing on this screen anybody is actually looking at.
        The rule across the top and the lavender behind it are already enough
        to say the page has ended, so the type is sized like the caption it
        is and the air is cut to what keeps it off both edges. It gives a
        little more back on a short phone.

        No button on it. There is nothing to press — the desktop is not
        somewhere this app can send anybody — and a button that only repeated
        the sentence would be the screen pretending to offer a way out. */}
    <div className="-mx-page -mb-page border-t border-[#DED6F4] bg-[#EDE7F6] px-page pt-7 pb-[calc(env(safe-area-inset-bottom)_+_1.25rem)] short:pt-5 text-center">
      <p className="text-[17px] font-medium leading-tight text-[#5B3FA8]">
        Switch to desktop
      </p>
      <p className="mt-0.5 text-[13px] leading-[1.4] text-[#6F638C]">
        To setup and manage these features
      </p>
    </div>
  </div>
);

export default MyBrandMobilePage;
